import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import Scrollbar from 'react-scrollbars-custom';

import { Switch } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { isMinor } from '@libs/NwMoment';

import { useLayoutContext, useLayoutContextDispatch } from '@contexts/LayoutContext';
import { useModelContext } from '@contexts/ModelContext';

import { forceReloadModels, modelFullName } from '@hooks/API/Contact/UseContactsList';
import { useShowSeason } from '@hooks/API/ShowSeasons/UseShowSeasons';
import useLocalStorageState from '@hooks/Utils/UseLocalStorageState';

import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ModelAvailabilityIconDrawer from '@components/ContactsSelector/ModelsSelector/ModelsAvailability/ModelAvailabilityIconDrawer';
import ModelCardsWrapper from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsCards/ModelCardsWrapper';
import ModelsTableWrapper from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsTable/ModelsTableWrapper';
import ModelsTableAccountingWrapper from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsTableAccounting/ModelsTableAccountingWrapper';
import { ModelsWallAccountingFooter } from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWallAccountingFooter/ModelsWallAccountingFooter';
import ModelsWallAccountingHeader from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWallAccountingHeader/ModelsWallAccountingHeader';
import { ModelsWallFooter } from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWallFooter/ModelsWallFooter';
import ModelsWallHeader from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWallHeader/ModelsWallHeader';
import ModelsWallMiniHeader from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWallHeader/ModelsWallMiniHeader';
import CenteredBlock from '@components/Gui/CenteredBlock';
import EmptyState from '@components/Gui/EmptyState';
import { WallContainer } from '@components/Gui/StyledComponent';
import StyleVariables from '@components/Gui/StyleVariables.js';
import { WallLineItem } from '@components/Gui/WallLineItem';

import { ShowSeasonFilterProvider } from '@containers/Booking/ShowSeasons/ShowSeasonFilterContext';
import { buildSeasonModelsByModelId, getSeasonDisplayName } from '@containers/Booking/ShowSeasons/showSeasonsHelpers';

const ScrollbarContainer = styled.div`
    border: ${StyleVariables.DefaultBorder};
    border-radius: 4px;
    /* il sider padre è una colonna flex: la lista prende l'altezza lasciata da filtri e switch */
    flex: 1 1 0;
    min-height: 100px;
    position: relative;

    .empty-wall-line-item {
        height: 1.75rem;
        width: 100%;
    }

    .list-counter {
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 10;
        background-color: ${(props) => props.theme.PrimaryColor}DC;
        color: #fff;
        font-size: ${(props) => props.theme.FontSizeSm};
        padding: 0.25rem 1rem;
        border-radius: 0 0 4px 4px;
        width: 100%;
        font-weight: bold;
    }
`;

const UnderScrollbar = styled.div`
    margin-top: 10px;
    text-align: center;
`;

const ModelsWall = ({ inSideBar, isLoading, list, listNotFeaturesFiltered }) => {
    const { selectedModel, onSelectModel } = useModelContext();
    const navigate = useNavigate();

    const { selection, additional, actions, layout, context } = useContactsSelector();
    const cs_dispatch = useContactsSelectorDispatch();

    const { area, scope } = context;
    const { listType, quickFilters, activeSet } = selection;
    const { view } = layout;

    const layoutContext = useLayoutContext();
    const lc_dispatch = useLayoutContextDispatch();
    const { showCollapsibleSidebar, hasCollapsibleSidebar, isMobile, breakpoints } = layoutContext;

    const { multipleSelectionActive, selectedItemsIDs, availabilityDrawerModelId } = actions;

    const { disabledItems } = additional;

    const showSeasonFilterId = quickFilters.showSeasonFilter || null;
    const { data: seasonDetails, isFetching: isLoadingSeasonDetails } = useShowSeason(showSeasonFilterId);
    const showSeasonFilterContextValue = useMemo(
        () => ({
            seasonId: showSeasonFilterId,
            seasonName: seasonDetails ? getSeasonDisplayName(seasonDetails) : null,
            seasonModelsByModelId: buildSeasonModelsByModelId(seasonDetails),
            isLoadingSeasonDetails,
        }),
        [showSeasonFilterId, seasonDetails, isLoadingSeasonDetails]
    );

    const [scrollPosition, setScrollPosition] = useLocalStorageState(`modelswall_listScrollPosition`, 0);
    const [motherAgencyInList, setMotherAgencyInList] = useLocalStorageState(`modelswall_motherAgencyInList`, false);
    const [recalcScroll, setRecalcScroll] = useState(false);

    const handleClickOnList = (itemId) => {
        if (scope === 'booking') {
            navigate(`/schedule/${listType}/${activeSet}/${itemId}`);
            //onSelectModel(itemId)
        } else {
            if (area === 'accounting') {
                navigate(`/accounting/model/${itemId}`);
            } else {
                navigate(`/model/${itemId}/${listType}/${activeSet}`);
            }
        }
        if (hasCollapsibleSidebar && showCollapsibleSidebar) {
            lc_dispatch({
                type: 'ToggleSidebar',
            });
        }
    };

    const handleCloseModelAvailabilityDrawer = () => {
        cs_dispatch({
            type: 'CloseModelAvailabilityDrawer',
        });
    };

    const handleReload = () => {
        forceReloadModels();
    };

    useEffect(() => {
        if (disabledItems) {
            const selectedWithoutDisabled = selectedItemsIDs.filter(
                (modelId) => !disabledItems.find((item) => item === modelId)
            );
            cs_dispatch({
                type: 'UpdateSelectedItems',
                newlist: [...selectedWithoutDisabled],
            });
        }
    }, [disabledItems]);

    useEffect(() => {
        if (recalcScroll) {
            setRecalcScroll(false);
            setScrollPosition(itemScrollPosition() - 100);
        }
    }, [motherAgencyInList]);

    useEffect(() => {
        if (list && list.length && selectedModel) {
            // && !listUpdated
            setScrollPosition(itemScrollPosition() - 100);
        }
    }, [list, selectedModel]);

    const resetScroll = () => {
        setScrollPosition(0);
    };

    const beforeChangeSort = () => {
        setScrollPosition(0);
    };

    const handleScrollStop = (scrollValues) => {
        setScrollPosition(scrollValues.scrollTop);
    };

    const itemScrollPosition = () => {
        const listItemHeight = motherAgencyInList ? 42 : 34;
        if (selectedModel) {
            const index = list.findIndex((item) => {
                return item.ID === Number(selectedModel);
            });
            if (index > 0) return listItemHeight * index;
        }
        return 0;
    };

    const OnAddTag = async (tagID) => {
        await Axios.post(`/contacts/addtagmulti?idt=${tagID.ID}`, selectedItemsIDs);
        handleReload();
    };

    const OnRemoveTag = async (tagID) => {
        await Axios.post(`/contacts/removetagmulti?idt=${tagID.ID}`, selectedItemsIDs);
        handleReload();
    };

    if (scope === 'accounting-search') {
        return (
            <WallContainer>
                <ModelsWallAccountingHeader
                    isLoading={isLoading}
                    modelList={list}
                    //beforeChangeSort={beforeChangeSort}
                />
                <ModelsTableAccountingWrapper
                    footer={
                        breakpoints > 3 &&
                        multipleSelectionActive && <ModelsWallAccountingFooter models={list} />
                    }
                    isLoading={isLoading}
                    models={list}
                />
            </WallContainer>
        );
    }

    if (inSideBar) {
        return (
            <>
                <ModelsWallMiniHeader onAfterChangeFilters={resetScroll} />
                {list.length ? (
                    <>
                        <ScrollbarContainer motherAgencyInList={motherAgencyInList}>
                            <Scrollbar
                                onScrollStop={handleScrollStop}
                                scrollTop={scrollPosition}
                                style={{ width: '100%', height: '100%', minHeight: 100 }}
                            >
                                {list.map((item) => (
                                    <WallLineItem
                                        isSelected={selectedModel && item.ID === Number(selectedModel)}
                                        key={item.ID}
                                        onClick={() => handleClickOnList(item.ID)}
                                        text={modelFullName(item)}
                                        prefix={quickFilters.sort === 'ID' ? item.ID : null}
                                        smallText={item.MAN || (item.R ? 'represented' : 'no mother agency')}
                                        isMinor={isMinor(item.B)}
                                        showSmallText={motherAgencyInList && !isMobile}
                                        isApproved={item.A}
                                    />
                                ))}
                                <div className="empty-wall-line-item"></div>
                            </Scrollbar>
                            <div className="list-counter">{list.length} models</div>
                        </ScrollbarContainer>
                        {!isMobile && (
                            <UnderScrollbar>
                                <small>
                                    <Switch
                                        checkedChildren="show"
                                        onChange={() => {
                                            setRecalcScroll(true);
                                            setMotherAgencyInList(!motherAgencyInList);
                                        }}
                                        checked={motherAgencyInList}
                                        unCheckedChildren="hide"
                                    />
                                    &nbsp;mother agencies
                                </small>
                            </UnderScrollbar>
                        )}
                    </>
                ) : (
                    <CenteredBlock fullpage={true}>
                        <EmptyState message="no models fitting your search" />
                    </CenteredBlock>
                )}
            </>
        );
    }

    return (
        <WallContainer>
            <ModelsWallHeader
                filteredList={list}
                isLoading={isLoading}
                modelList={listNotFeaturesFiltered}
                beforeChangeSort={beforeChangeSort}
            />
            <ShowSeasonFilterProvider value={showSeasonFilterContextValue}>
                {view.current === 'table' ? (
                    <ModelsTableWrapper
                        footer={
                            breakpoints > 3 &&
                            multipleSelectionActive && (
                                <ModelsWallFooter
                                    onAddTag={OnAddTag}
                                    onRemoveTag={OnRemoveTag}
                                    models={list}
                                    isLoading={isLoading}
                                />
                            )
                        }
                        isLoading={isLoading}
                        models={list}
                    />
                ) : (
                    <ModelCardsWrapper
                        footer={
                            breakpoints > 3 &&
                            multipleSelectionActive && (
                                <ModelsWallFooter
                                    onAddTag={OnAddTag}
                                    onRemoveTag={OnRemoveTag}
                                    models={list}
                                    isLoading={isLoading}
                                />
                            )
                        }
                        isLoading={isLoading}
                        models={list}
                    />
                )}
            </ShowSeasonFilterProvider>
            {availabilityDrawerModelId && (
                <ModelAvailabilityIconDrawer
                    modelId={availabilityDrawerModelId}
                    onClose={handleCloseModelAvailabilityDrawer}
                />
            )}
        </WallContainer>
    );
};

export default ModelsWall;
